/**
 * STEP 9: REVIEW & COMPLETE
 * 
 * Final checklist verification before generating the estimate.
 * Ensures all required data is captured.
 * 
 * Theme: Spec Ops
 * Based on Document 5 specification
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepContainer from './StepContainer';
import { 
  COMPLETION_CHECKLIST, 
  runChecklistValidation, 
  allRequiredItemsPass 
} from '../constants/completionChecklist';
import { DEFAULT_FIELD_VISIT_SETTINGS } from '../constants/settingsDefaults';
import { 
  Check, 
  X, 
  AlertTriangle, 
  FileText, 
  Loader2, 
  ChevronRight,
  Rocket,
  Lock,
  ClipboardCheck,
  MessageSquare
} from 'lucide-react';

const Step9ReviewComplete = ({ 
  fieldVisit = {},
  completedSteps = [],
  settings = DEFAULT_FIELD_VISIT_SETTINGS,
  onUpdate,
  onComplete,
  onGenerateEstimate,
  onSave,
  onPrev,
  saving = false,
  isComplete = false,
  validationErrors = [],
  goToStep,
}) => {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const [generatingEstimate, setGeneratingEstimate] = useState(false);
  const [estimatorNotes, setEstimatorNotes] = useState(fieldVisit?.review?.estimator_notes || '');

  // Run checklist validation
  const checklistResults = useMemo(() => {
    return runChecklistValidation(fieldVisit, settings);
  }, [fieldVisit, settings]);

  // Check if all required items pass
  const allRequiredPassed = useMemo(() => {
    return checklistResults
      .filter(item => !item.optional)
      .every(item => item.passed);
  }, [checklistResults]);

  const completionCount = checklistResults.filter(item => item.passed).length;
  const totalItems = checklistResults.length;

  // Update estimator notes
  useEffect(() => {
    if (estimatorNotes !== fieldVisit?.review?.estimator_notes) {
      onUpdate?.('review', { 
        ...fieldVisit?.review,
        estimator_notes: estimatorNotes 
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatorNotes]);

  // Handle step navigation
  const handleGoToStep = (step) => {
    if (goToStep) {
      goToStep(step);
    }
  };

  // Handle complete field visit
  const handleComplete = async () => {
    if (!allRequiredPassed || isComplete) return;
    
    setCompleting(true);
    try {
      await onComplete?.();
    } catch (err) {
      console.error('Failed to complete field visit:', err);
    } finally {
      setCompleting(false);
    }
  };

  // Handle generate estimate
  const handleGenerateEstimate = async () => {
    if (!isComplete) return;
    
    setGeneratingEstimate(true);
    try {
      const result = await onGenerateEstimate?.();
      if (result?.estimate_id) {
        // Navigate to estimate page
        navigate(`/estimating?estimate=${result.estimate_id}`);
      }
    } catch (err) {
      console.error('Failed to generate estimate:', err);
    } finally {
      setGeneratingEstimate(false);
    }
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    onSave?.();
    navigate(-1);
  };

  return (
    <StepContainer
      step={9}
      onPrev={onPrev}
      onNext={null}
      onSave={!isComplete ? onSave : null}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
      showNav={!isComplete}
    >
      <div className="space-y-6 max-w-3xl">
        {/* Status Banner */}
        <div className={`
          p-4 rounded border flex items-start gap-4
          ${allRequiredPassed 
            ? 'bg-emerald-500/10 border-emerald-500/40' 
            : 'bg-amber-500/10 border-amber-500/40'
          }
        `}>
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
            ${allRequiredPassed ? 'bg-emerald-500/20' : 'bg-amber-500/20'}
          `}>
            {allRequiredPassed ? (
              <Check className="w-6 h-6 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            )}
          </div>
          <div>
            <h3 className={`font-bold text-lg ${allRequiredPassed ? 'text-emerald-400' : 'text-amber-400'}`}>
              {allRequiredPassed ? 'Ready to Complete' : 'Checklist Incomplete'}
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              {completionCount} of {totalItems} items complete
              {!allRequiredPassed && (
                <span className="ml-2 text-amber-400">
                  • Complete all required items to proceed
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Completion Checklist */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2 flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Completion Checklist
          </h3>
          
          <div className="space-y-2" data-testid="completion-checklist">
            {checklistResults.map((item) => (
              <div 
                key={item.id}
                className={`
                  flex items-center justify-between p-3 border rounded transition-all
                  ${item.passed 
                    ? 'border-emerald-500/30 bg-emerald-500/5' 
                    : item.optional 
                      ? 'border-amber-500/30 bg-amber-500/5'
                      : 'border-red-500/30 bg-red-500/5'
                  }
                `}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Status Icon */}
                  {item.passed ? (
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-black" />
                    </div>
                  ) : item.optional ? (
                    <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-3 h-3 text-black" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-red-500 flex-shrink-0" />
                  )}
                  
                  {/* Item Details */}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium ${item.passed ? 'text-emerald-400' : item.optional ? 'text-amber-400' : 'text-red-400'}`}>
                      {item.label}
                      {item.optional && <span className="text-xs ml-2 opacity-70">(optional)</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">
                      {item.passed ? item.summary : item.errorMessage}
                    </div>
                  </div>
                </div>
                
                {/* Go To Step Button */}
                <button
                  onClick={() => handleGoToStep(item.step)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-slate-200 
                    hover:bg-slate-700 rounded transition-colors flex-shrink-0"
                >
                  Step {item.step}
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Estimator Notes */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-300 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Estimator Notes
            <span className="text-slate-500 font-normal">(Internal - not shown on estimate)</span>
          </label>
          <textarea
            value={estimatorNotes}
            onChange={(e) => setEstimatorNotes(e.target.value)}
            disabled={isComplete}
            rows={4}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-700 text-slate-100 rounded
              focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            placeholder="Notes for yourself or your team about this project...
Good clients, realistic expectations. Wife makes decisions on finishes.
Referred by Mike Johnson - priority scheduling."
            data-testid="estimator-notes-input"
          />
        </div>

        {/* Action Section */}
        <div className="space-y-4 pt-4 border-t border-slate-700">
          {!isComplete ? (
            <>
              {/* Ready to Generate Info */}
              {allRequiredPassed && (
                <div className="p-4 bg-slate-800/50 border border-slate-700 rounded space-y-2">
                  <p className="text-slate-300 text-sm">
                    Clicking <strong>&quot;Complete Field Visit&quot;</strong> will:
                  </p>
                  <ul className="text-xs text-slate-400 space-y-1 ml-4">
                    <li className="flex items-center gap-2">
                      <Lock className="w-3 h-3" />
                      Lock this field visit from further edits
                    </li>
                    <li className="flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      Enable estimate generation
                    </li>
                  </ul>
                </div>
              )}

              {/* Complete Button */}
              <button
                onClick={handleComplete}
                disabled={!allRequiredPassed || completing}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg
                  disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2
                  transition-colors"
                data-testid="complete-field-visit-button"
              >
                {completing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Complete Field Visit
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="space-y-4">
              {/* Completed Status */}
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/40 rounded text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-emerald-400 font-bold text-lg">Field Visit Complete</p>
                <p className="text-sm text-slate-400 mt-1">
                  Locked at {new Date(fieldVisit?.locked_at || Date.now()).toLocaleString()}
                </p>
              </div>
              
              {/* Generate Estimate Button */}
              {!fieldVisit?.estimate_id ? (
                <button
                  onClick={handleGenerateEstimate}
                  disabled={generatingEstimate}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-500 text-white font-bold text-lg
                    disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                  data-testid="generate-estimate-button"
                >
                  {generatingEstimate ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Estimate...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      Generate Estimate
                    </>
                  )}
                </button>
              ) : (
                <div className="p-4 bg-purple-500/10 border border-purple-500/40 rounded text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-purple-400" />
                  </div>
                  <p className="text-purple-400 font-bold">Estimate Generated</p>
                  <p className="text-sm text-slate-400 mt-1">
                    ID: {fieldVisit.estimate_id}
                  </p>
                  <button 
                    onClick={() => navigate(`/estimating?estimate=${fieldVisit.estimate_id}`)}
                    className="mt-3 px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium
                      transition-colors"
                  >
                    View Estimate →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Save & Close Option */}
          {!isComplete && (
            <button
              onClick={handleSaveAndClose}
              className="w-full py-3 border border-slate-700 text-slate-400 hover:bg-slate-800 
                font-medium transition-colors"
            >
              Save & Close Without Completing
            </button>
          )}
        </div>
      </div>
    </StepContainer>
  );
};

export default Step9ReviewComplete;
